
import {Component, Vue, Watch} from "vue-property-decorator";
import SimpleDialog from "@/components/SimpleDialog.vue";
import KeyTransit from "@/components/keys/actions/KeyTransit.vue";
import KeyRetrieve from "@/components/keys/actions/KeyRetrieve.vue";
import HandToHand from "@/components/keys/actions/HandToHand.vue";
import KeyWithdraw from "@/components/keys/actions/KeyWithdraw.vue";
import KeyWithdrawConnected from "@/components/keys/actions/KeyWithdrawConnected.vue";
import KeyDropAgency from "@/components/keys/actions/KeyDropAgency.vue";
import KeyDropConnected from "@/components/keys/actions/KeyDropConnected.vue";
import KeyStatus from "@/components/keys/KeyStatus.vue";
import KeyEdit from "@/components/keys/KeyEdit.vue";
import {Getter} from "vuex-class";
import { removeVuetifyFileInputBug } from "@/utils/misc";
import router from "@/router";
import UserListAvatar from "@/components/users/UserListAvatar.vue";
import KeyArchive from "@/components/keys/actions/KeyArchive.vue";
import moment from "moment";
import KeyReturningDate from "@/components/keys/actions/KeyReturningDate.vue";
import KeyCarousel from "@/components/keys/KeyCarousel.vue";
import {dataPicture} from "@/utils/pictureFonction";
import RelayGiveKeyToGuest from "@/components/relays/RelayGiveKeyToGuest.vue";
import { CONTEXT_TYPES, IContext } from "@/store/modules/context";
import ReserveLocation from "@/components/keys/ReserveLocation.vue";
import { mdiQrcodeEdit, mdiQrcodePlus } from "@mdi/js";
import SvgIcon from "@jamescoyle/vue-icon";
import KeyActivity from "@/components/keys/KeyActivity.vue";
import MylobyAutocomplete from "@/components/generic/autocomplete.vue";
import { getTokenLink } from "@/utils/misc";

@Component({
  components: {
    KeyEdit,
    MylobyAutocomplete,
    KeyActivity,
    KeyCarousel,
    SimpleDialog,
    UserListAvatar,
    KeyArchive,
    KeyStatus,
    KeyTransit,
    KeyRetrieve,
    KeyDropAgency,
    KeyDropConnected,
    KeyWithdraw,
    KeyWithdrawConnected,
    HandToHand,
    KeyReturningDate,
    RelayGiveKeyToGuest,
    ReserveLocation,
    SvgIcon,
  },
})
export default class Key extends Vue {

  get lang(): string {
    return this.$root.$i18n.locale;
  }

  get canSeeKeyActivity(): boolean {
    return this.canDoInEnvCompany("KEY_ACTIVITY");
  }

  get isArchived(): boolean {
    return !!this.key?.archivedAt;
  }

  get isCompanyBlocked(): boolean {
    return !!this.key?.company?.blockedAt;
  }

  get archivedText(): string {
    if (this.isAdmin) {
      return "key.archived";
    } else if (!this.hasRight("UNARCHIVE_KEY")) {
      return "key.archivedGuest";
    } else {
      return "key.archived";
    }
  }

  get hasArchiveRight(): boolean {
    return this.hasRight("ARCHIVE_KEY") || this.hasRight("UNARCHIVE_KEY");
  }

  get isCurrentUserHolder(): boolean {
    return this.key.holder ? this.key.holder.id === this.currentUser?.id : false;
  }

  get userHasKey() {
    return this.key?.KeyHolding?.find((kH) => !kH.endDate)?.userId === this.currentUser?.id;
  }

  get keyReferenceRelay(): any {
    return this.key?.Relays?.find((relay) => relay.type === "AGENCY") || this.key?.Relays?.[0];
  }

  get getCurrentUserHandToHandTransaction(): any | undefined {
    return this.key?.HandToHand?.find((handToHand) => handToHand?.targetUserId === this.currentUser?.id);
  }

  get routerParams(): any {
      return this.$route.params;
  }

  get displayReserveLocationButton(): boolean {
    return this.locationLock && this.hasRight("EDIT_KEY_RESERVED_LOCATION");
  }

  get currentKeyBooking(): string {
    if (!this.key || !this.key.currentBooking) {
      return "";
    }
    const startDate = this.key.currentBooking.startDate;
    const endDate = this.key.currentBooking.endDate;
    const parseStartDate = moment(startDate).format("YYYY-MM-DD HH:mm");
    const parseEndDate = moment(endDate).format("YYYY-MM-DD HH:mm");
    return parseStartDate + "  -  " + parseEndDate;
  }

  get getKeyParentIfExists(): string {
    return this.key.Parent?.name ? `${this.key.Parent?.name} | ` : "";
  }

  get isManager(): boolean {
    return !!this.key?.legitimateKeyAccess?.keyManagementId;
  }

  get isGuest(): boolean {
    return this.isValidAccess(this.key?.legitimateKeyAccess);
  }

  get connectedUserHasKeyAccess() {
    return this.isManager || this.isGuest || this.userHasKey;
  }

  @Getter private currentUser: any;
  @Getter private isAdmin: boolean;
  @Getter private canDoInEnvCompany: (right: string) => boolean;
  @Getter private context: IContext|null;
  @Getter private isBookedKeyAvailable: (key: any) => boolean;

  private removeVuetifyFileInputBug: (document: any) => void = removeVuetifyFileInputBug;
  private token: string = null;
  private keyId: number = null;
  private key: any = {};
  private loadingGetKey: boolean = true;
  private editKeyInfoDialog: boolean = false;
  private avatar: any = null;
  private displayReserveLocationModal: boolean = false;
  private displayChooseHour: boolean = false;
  private endCountdown: boolean = false;
  private componentKey: number = 0;
  private heicFile: any = null;
  private showCarouselDialog: boolean = false;
  private keyPictures: any[] = [];
  private s3Src: string | null = null;
  private subTab: string = null;
  private loadingSubTab: boolean = false;
  private mdiQrcodeEdit: string = mdiQrcodeEdit;
  private mdiQrcodePlus: string = mdiQrcodePlus;
  private showKeyActivityModal: boolean = false;
  private locationLock: boolean = false;
  private readonly getTokenLink = getTokenLink;


  @Watch("key", { immediate: true, deep: true })
  public onCurrentKeyChanged(newValue: any, oldValue: any) {
    if (newValue?.id && newValue !== oldValue) {
      if (this.keyReferenceRelay) {
        this.fetchRelayOptions();
      }
    }
  }

  private async fetchRelayOptions(): Promise<void> {
    // Check if location can be locked in either relay or building
    this.locationLock = await Promise.all([
      // Reference relay
      this.$store.dispatch("options/readOptionValueByBuilding", {
        buildingId: this.keyReferenceRelay.buildingId || this.keyReferenceRelay.Building.id,
        optionTypeLabel: "locationLock",
      }),
      // Linked building
      this.key.buildingId ? this.$store.dispatch("options/readOptionValueByBuilding", {
        buildingId: this.key.buildingId,
        optionTypeLabel: "locationLock",
      }) : false,
    ]).then((res) => res.some(Boolean));
  }

  private handlerSavePicture(newPicture: any) {
    this.avatar = newPicture.avatar;
    this.heicFile = newPicture.heicFile;
    const profile = false;
    const key = {id: this.keyId, name: this.key.name};
    this.savePicture(key, newPicture, profile);
  }

  private getKeyPictures(): void {
    if (this.key?.id) {
      this.getKeyPictureById().then(() => {
        this.showCarouselDialog = true;
      });
    }
  }

  private lateKey = (date) => moment(new Date(date), "YYYY-MM-DDTHH:mm:ss")
    .isBefore(moment().format("YYYY-MM-DDTHH:mm:ss"))

  private getTime = (date?: string) => date
    ? moment(new Date(date), "YYYY-MM-DD HH:mm:ss").valueOf()
    : moment().valueOf()

  // whether or not show days in countdown
  private showDays = (date) => Math.abs(moment(new Date(date), "YYYY-MM-DD HH:mm:ss").valueOf() - moment().valueOf())
    > 24 * 60 * 60 * 1000

  private getDifference(date): string {
    date = new Date(date);
    const a = moment(date, "YYYY-MM-DD hh:mm");
    const b = moment();
    const diffDays = b.diff(a, "days");
    const diffHours = b.diff(a, "hours");
    const diffMinutes = b.diff(a, "minutes");
    if (diffMinutes < 60) {
      return diffMinutes % 60 + " " + String(this.$tc("countDown.minutes", diffMinutes));
    } else if (diffHours < 24) {
      return diffHours % 24 + " " + String(this.$tc("countDown.hours", diffHours)) + ", "
        + diffMinutes % 60 + " " + String(this.$tc("countDown.minutes", diffMinutes));
    } else {
      return diffDays + " " + String(this.$tc("countDown.days", diffDays)) + ", "
        + diffHours % 24 + " " + String(this.$tc("countDown.hours", diffHours)) + ", "
        + diffMinutes % 60 + " " + String(this.$tc("countDown.minutes", diffMinutes));
    }
  }

  // extend holding key
  private validateTime(date) {
    this.displayChooseHour = false;
    this.$store.dispatch("keys/extendKeyHolding", {
      keyId: this.key.id,
      newEstimatedEndDate: (new Date(date)).toISOString().substr(0, 19),
    }).then(() => {
      this.$store.commit("alerts/displaySuccess", {
        msg: this.$i18n?.t("alerts.key.addTimeSuccess", {
          key: this.key?.name,
          time: moment(date).format("DD/MM/YYYY HH:mm"),
        }),
      });
      this.reset();
    });
  }

  private isKeyBooked = (key) => key.currentBooking ?
    moment().isAfter(moment(key.currentBooking.startDate))
    && moment().isBefore(moment(key.currentBooking.endDate)) : false

  private isValidAccess = (access: any) => !!access && (access.countAccessLeft === -1 || access.countAccessLeft >= 1)
    && (access.startDate === null || new Date(access.startDate) <= new Date())
    && (access.endDate === null || new Date(access.endDate) >= new Date())

  private displayCodeName(key) {
    if (key.backupId) {
      let codeName;
      try {
        const s = key.locationDisplayed.codeName.split("-");
        codeName = s[s.length - 1];
      } catch {
        codeName = "";
      }
      return codeName;
    } else {
      return key.locationDisplayed.codeName;
    }
  }

  private displayBoxName(key) {
    return key.locationDisplayed.Box.name;
  }

  private mounted(): void {
      this.setPageKeyId(Number(this.$route.params.id));
  }

  @Watch("$route.params.id")
  private onRouteIdChanged(newId: string) {
    // Reset des infos de la page si le keyId de l'URL change
    this.setPageKeyId(Number(newId));
  }

  private setPageKeyId(keyId: number) {
      this.keyId = keyId;
      if (isNaN(this.keyId)) {
        this.redirectDashboard();
      }
      this.reset();
  }

  private redirectDashboard(): void {
    if (this.isAdmin) {
      router.push({name: "admin-keys"});
    } else {
      router.push({name: "keys"});
    }
  }

  private beforeDestroy(): void {
    // If the context is location based, remove it
    if (this.context?.type === CONTEXT_TYPES.LOCATION) {
      this.clearContext();
    }
  }

  private beforeUpdate(): void {
    this.removeVuetifyFileInputBug(document);
  }

  private clearContext(): void {
    this.$store.dispatch("context/clearContext");
  }

  private reset() {
    this.closeEditKeyInfoDialog();
    this.forceImageKeyRender();
    this.getKeyById();
  }

  private getKeyById(): Promise<void> {
    this.loadingGetKey = true;
    return this.$store.dispatch("keys/getById", {
      id: this.keyId,
      options: {
        picture: false,
        relays: true,
        reservedLocation: true,
        company: true,
        holder: true,
        currentLocation: true,
        ongoingSubscriptions: true,
        building: true,
      },
    }).then(async (key) => {

      // Update global env companyId with current entity company
      await this.$store.dispatch("changeEnvCompanyId", key.companyId);
      key.isFollowed = await this.$store.dispatch("keys/getIsFollowedKey", {
        keyId: this.keyId,
        userId: this.currentUser.id,
      });

      key.relayId = (key?.Relays?.find((relay) => relay.type === "AGENCY") || key?.Relays?.[0])?.id;
      key.Relays = key?.Relays?.map((relay) => ({
        value: relay?.id,
        ...relay,
      }));
      key.newTags = key.Tags;
      key.reservedLocation = key?.ReservedLocations?.[0];
      key.locationDisplayed = key?.currentLocation || key?.ReservedLocations?.[0];
      key.estimatedEndDate = key.KeyHolding[0]?.estimatedEndDate;
      key.newGuests = [];
      this.key = key;
    }).catch((error) => {
      if (error.response?.data?.error !== "Unauthorized") {
        this.$store.commit("alerts/displayHttpError", { error });
      }
      this.redirectDashboard();
    }).finally(() => {
      this.loadingGetKey = false;
    });
  }

  private displayInvitationMessage(data: {
    endDate: Date,
    startDate: Date,
    countAccessLeft: number,
  }) {
    const invitationType = data.countAccessLeft === -1
      ? this.$t("invitation.permanent")
      : this.$t("invitation.punctual");
    const plageHoraires = data.endDate
      ? this.$t("invitation.periodWithBeginAndEnd", {
          startDate: this.$options.filters.formatDate(data.startDate),
          endDate: this.$options.filters.formatDate(new Date(data.endDate)),
        })
      : "";
    return this.$t("invitation.notification", {
      invitationType,
      plageHoraires,
    });
  }

  private goHandToHandScan(): void {
    this.$router.push({
      name: "key-hand-to-hand-scan",
      params: {
        keyId: this.key.id,
        token: this.getCurrentUserHandToHandTransaction?.token,
      },
    });
  }

  private async toggleFollowed(item: any, event: Event) {
    if (this.isCompanyBlocked || this.isArchived) { return; }
    const checked = (event.target as HTMLInputElement).checked;
    if (!checked) {
      await this.$store.dispatch("keys/removeFollowedKey", {keyId: item.id, userId: this.currentUser.id})
        .then(() => { item.isFollowed = false; })
        .catch((err) => {
        this.$store.commit("alerts/displayError", {
        msg: err,
        });
    });
    } else {
      await this.$store.dispatch("keys/addFollowedKey", {keyId: item.id, userId: this.currentUser.id})
        .then(() => { item.isFollowed = true; })
        .catch((err) => {
        this.$store.commit("alerts/displayError", {
        msg: err,
        });
      });
    }
  }

  private async getKeyPictureById(): Promise<void> {
    await this.$store.dispatch("keys/getAllPictures", {
      id: this.key.id,
    }).then((res) => {
      this.keyPictures = res.pictures;
    }).catch((error) => {
      this.$store.commit("alerts/displayHttpError", { error });
    });
  }

  private async savePicture(key, imgInfo: any, profile) {
    const data = await dataPicture(
      key,
      imgInfo,
      profile,
      this.currentUser,
      this.heicFile,
      this.avatar,
    );
    const saveImageRes = await this.$store.dispatch("files/uploadKeysPicture",
      {formData: data.formData, keyId : key.id},
    );
    data.picture.name = saveImageRes.name;
    data.picture.picturePath = data.picture.picturePathFn(saveImageRes.name);
    return this.$store.dispatch("keys/addPicture", {
      picture: data.picture,
      keyId: key.id,
    }).then(() => {
      this.$store.commit("alerts/displaySuccess", {
        icon: "icon-picto_cle-partagees",
        msg: this.$t("keyPicture.add"),
      });
      this.closeEditKeyInfoDialog();
      this.getKeyById();
    });
  }

  private openEditDialog(): void {
    this.editKeyInfoDialog = true;
  }

  private closeEditKeyInfoDialog(): void {
    this.editKeyInfoDialog = false;
    this.key.newTags = this.key.Tags;
  }

  private forceImageKeyRender() {
    this.componentKey += 1;
  }

  private hasRight(right: string): boolean {
    return this.canDoInEnvCompany(right);
  }

  private hasCategoryRight(category: string): boolean {
    if (category === "actionsLikeManager") {
      const rights = [
        "DROP_KEY",
        "WITHDRAW_KEY",
        "RETRIEVE_KEY",
        "ARCHIVE_KEY",
        "UNARCHIVE_KEY",
      ];
      return rights.some((right) => this.canDoInEnvCompany(right));
    } else if (category === "editKey") {
      const editRights = [
        "EDIT_KEY_NAME",
        "EDIT_KEY_DESCRIPTION",
        "EDIT_KEY_TAGS",
        "EDIT_KEY_IMAGE",
        "EDIT_KEY_PRIVATE_IMAGES",
        "EDIT_KEY_RELAY",
        "EDIT_KEY_RESERVED_LOCATION",
      ];
      return editRights.some((right) => this.canDoInEnvCompany(right));
    }
  }
  private showKeyActivity() {
    this.showKeyActivityModal = true;
  }

  private hideKeyActivity() {
    this.showKeyActivityModal = false;
  }
}
